import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AutomotiveAftermarketListService } from './automotive-aftermarket-list.service';
import {
  AutomotiveAftermarketListInitialState,
  AutomotiveAftermarketListLoad,
} from './automotive-aftermarket-list.actions';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { LoadPrimary, TransportOrderPrimary } from '../transport/models';
import { CustomHttpErrorResponse, Error } from '../../../../common/constants';
import { isSomeFilterApplied } from '../../../../common/utils';
import { AutomotiveAftermarketTypeViewType, AutomotiveAftermarketListRequestModel } from './models';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AutomotiveAftermarketCardItem {
  webContentTypeView: AutomotiveAftermarketTypeViewType;
  content: TransportOrderPrimary | LoadPrimary;
}

export interface AutomotiveAftermarketCard {
  items: AutomotiveAftermarketCardItem[];
}

export interface AutomotiveAftermarketListStateModel {
  items: AutomotiveAftermarketCardItem[];
  page: number;
  size: number;
  isMoreButtonVisible: boolean;
  isAnyFilterApplied: boolean;
  isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff: boolean;
  loaded: boolean;
}

const initialState: AutomotiveAftermarketListStateModel = {
  items: [],
  page: 0,
  size: 20,
  isMoreButtonVisible: false,
  isAnyFilterApplied: false,
  isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff: false,
  loaded: false,
};

@State<AutomotiveAftermarketListStateModel>({
  name: 'automotiveAftermarketList',
  defaults: initialState,
})
@Injectable()
export class AutomotiveAftermarketListState {
  constructor(
    private automotiveAftermarketListService: AutomotiveAftermarketListService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: AutomotiveAftermarketListStateModel): AutomotiveAftermarketListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: AutomotiveAftermarketListStateModel): AutomotiveAftermarketCardItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: AutomotiveAftermarketListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isMoreButtonVisible(state: AutomotiveAftermarketListStateModel): boolean {
    return state.isMoreButtonVisible;
  }

  @Selector()
  static isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff(state: AutomotiveAftermarketListStateModel): boolean {
    return state.isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff;
  }

  @Selector()
  static isAnyFilterApplied(state: AutomotiveAftermarketListStateModel): boolean {
    return state.isAnyFilterApplied;
  }

  @Action(AutomotiveAftermarketListLoad)
  getAutomotiveAftermarketList(
    ctx: StateContext<AutomotiveAftermarketListStateModel>,
    { payload }: AutomotiveAftermarketListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketListService.getAutomotiveAftermarketList(payload).pipe(
      map((automotiveAftermarketCard: AutomotiveAftermarketCard) => {
        const { page, size: _size, tileType, ...filters } = payload;
        const isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff = !tileType.length;
        const isAnyFilterApplied =
          isSomeFilterApplied<Omit<Partial<AutomotiveAftermarketListRequestModel>, 'page' | 'size' | 'tileType'>>(
            filters,
          );

        ctx.patchState({
          items:
            page > 0 ? ctx.getState().items.concat(automotiveAftermarketCard.items) : automotiveAftermarketCard.items,
          isMoreButtonVisible: Boolean(automotiveAftermarketCard.items?.length),
          page,
          isAnyFilterApplied,
          isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketListInitialState)
  setInitialState(ctx: StateContext<AutomotiveAftermarketListInitialState>): void {
    ctx.setState(initialState);
  }
}
